import {
  Spacer,
  LaunchPage,
  sortByDateTime,
  Tag,
  PageDS,
  Avatar,
  MaterialUiTable,
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  ToastContainerDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import {
  selectDriversList,
  selectDriversListStatus,
  driversSlice,
} from "./driversSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import {
  GroupsOutlined,
  PersonOutlined,
  NoTransferOutlined,
} from "@qivia/ui/src/designSystem/materialUi/materialUi";
import {
  DriversListDisplayedType,
  DriversListType,
  EcoDrivingScore,
} from "./driversAPI";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { selectCompany } from "../homeSlice";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import {
  formatCarbonEmissionPerKilometer,
  formatCurrency,
  formatLiter,
  formatLiterPerOneHundredMileage,
} from "../../../libSlice/materialUiFormats";
import "../../../i18n/i18n";
import { useSearchParams } from "react-router-dom";
import { useRef } from "react";
import { DriverAccessRequestSummary } from "./driversAccessRequestSummary";

interface SharedInterfaceDriversProps {
  sidePanelAction?: (props: Partial<DriversListType>) => void;
  sidePanel?: JSX.Element;
  otherSupportPhones?: {
    name: string;
    phone: string;
  }[];
  topElement?: JSX.Element;
}

export const SharedInterfaceDrivers = (props: SharedInterfaceDriversProps) => {
  const { t } = useTranslation();
  const params = useParams<{ id?: string }>();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const driversListInit = useAppSelector(selectDriversList);
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const [rowHoverUuid, setRowHoverUuid] = useState<string | null>(null);
  const company = useAppSelector(selectCompany);

  const [listDisplayed, setListDisplayed] = useState<
    DriversListDisplayedType[] | null
  >(null);

  const [rowToSendToTop, setRowToSendToTop] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const fromNavigate = searchParams.get("fromNavigate") === "true";

  const hasExecutedFromNavigate = useRef(false);
  const shouldExecuteFromNavigate = useRef(false);

  if (fromNavigate && !shouldExecuteFromNavigate.current) {
    shouldExecuteFromNavigate.current = true;
  }

  const pageName = "drivers";

  const apiRef = useGridApiRef();
  const key = "customers-grid";
  const aggregation = {
    model: {
      name: "size",
      phone: "size",
      vehicleType: "size",
      status: "size",
      registrationNumber: "size",
      vehicleReference: "size",
      cardLast4Digits: "size",
      averagePricePerLiter: "avg",
      averageQuantity: "avg",
      averageMonthlyAmount: "avg",
      averageConsumption: "avg",
      averageCarbonEmissionPerKm: "avg",
      analyticCode1: "size",
      analyticCode2: "size",
      transactionsWithoutMileageCount: "avg",
      transactionsWithoutSupportingDocumentCount: "avg",
      transactionsCount: "sum",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof DriversListType, value: string) => {
      switch (key) {
        case "vehicleType":
          return t(`drivers.vehicle.${value}`);
        case "status":
          return t(`drivers.status.${value}`);
        case "ecoDrivingScore":
          return (value as unknown as EcoDrivingScore)?.ecoDrivingScore;
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "vehicleType",
        "status",
        "ecoDrivingScore",
      ] as (keyof DriversListDisplayedType)[],
    [],
  );

  const driversList: DriversListType[] = useMemo(() => {
    const rowsWithId = driversListInit.map((av) => {
      let newDict: DriversListType & { id: string } = {
        ...av,
        id: av.uuid,
      };
      Object.entries(av).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof DriversListDisplayedType)) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(
                  key as keyof DriversListDisplayedType,
                  value as string,
                )
              : "",
          };
        }
      });
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [driversListInit, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    void dispatch(driversSlice.actions.resetDriversAccessRequestsListStatus());
  }, [dispatch]);

  useEffect(() => {
    if (params.id) {
      const driverSelected = driversList.find((d) => d.uuid === params.id);
      const firstPath = location.pathname.split("/")[1]; // home or departments
      if (driverSelected) {
        navigate(`/${firstPath}/drivers/${driverSelected.uuid}`);
      }
    }
  }, [driversList, navigate, params.id, location.pathname]);

  useEffect(() => {
    if (driversList && driversListStatus === "success") {
      const driversDateTimeSorted = sortByDateTime(driversList, "desc");
      setListDisplayed(driversDateTimeSorted);
    }
  }, [dispatch, driversList, driversListStatus]);

  // Only after a navigate from an other page like transaction, we select the row to send to the top of the MaterialUiTable
  useEffect(() => {
    if (
      params.id &&
      listDisplayed &&
      driversList &&
      apiRef.current &&
      shouldExecuteFromNavigate.current &&
      !hasExecutedFromNavigate.current
    ) {
      hasExecutedFromNavigate.current = true;
      shouldExecuteFromNavigate.current = false;

      const driverInDb = driversList.find(
        (driver) => driver.uuid === params.id,
      );

      if (driverInDb) {
        apiRef.current.setRowSelectionModel([driverInDb.uuid]);
        setRowToSendToTop(driverInDb.uuid);
      }
    }
  }, [params.id, listDisplayed, driversList, apiRef]);

  useEffect(() => {
    if (rowToSendToTop) {
      setRowToSendToTop(null);
    }
  }, [rowToSendToTop]);

  const searchBarProps = {
    values: driversList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const, "date" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("drivers.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof DriversListDisplayedType]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row) => {
          const initials = value?.split(" ").map((row) => row.charAt(0));
          return (
            value && (
              <StyledRow>
                <Avatar
                  color={"mint"}
                  size={"S"}
                  firstName={initials[0]}
                  lastName={initials[initials.length - 1]}
                  isHover={rowHoverUuid === row["uuid"]}
                />
                <Spacer x={0.75} />
                {value}
              </StyledRow>
            )
          );
        },
      },
      phone: {
        type: "string",
        width: 200,
      },
      vehicleType: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) =>
          value && (
            <StyledRow>
              <Tag
                text={value}
                backgroundColor={colors["colors/button/secondary/hover"]}
                iconLeft={
                  value === "PERSONAL" ? (
                    <PersonOutlined />
                  ) : value === "OUT_OF_FLEET" ? (
                    <NoTransferOutlined />
                  ) : (
                    <GroupsOutlined />
                  )
                }
                colorHover={
                  rowHoverUuid === row["uuid"]
                    ? colors["colors/surfaces/background/background_level0"]
                    : undefined
                }
              />
            </StyledRow>
          ),
      },
      status: {
        type: "string",
        valueFormatter: (value: string, row) =>
          value && (
            <StyledRow>
              <Tag
                text={value}
                backgroundColor={
                  value === t("drivers.status.ACTIVE")
                    ? colors["colors/system/success/success_ultraLight"]
                    : colors["colors/surfaces/background/background_level2"]
                }
                textColor={
                  value === t("drivers.status.ACTIVE")
                    ? colors["colors/system/success/success_normal"]
                    : colors["colors/text/black"]
                }
                colorHover={
                  value !== t("drivers.status.ACTIVE") &&
                  rowHoverUuid === row["uuid"]
                    ? colors["colors/surfaces/background/background_level0"]
                    : undefined
                }
              />
            </StyledRow>
          ),
      },
      cardLast4Digits: {
        type: "string",
        valueFormatter: (value: string) => value && `** ${value}`,
      },
      registrationNumber: {
        type: "string",
      },
      vehicleReference: {
        type: "string",
        width: 250,
        valueFormatter: (value) =>
          value && (
            <StyledRow>
              <div style={{ width: "2rem" }}>
                <img
                  src={Car}
                  style={{ width: "100%", filter: "grayscale(100%)" }}
                  alt="Car"
                />
              </div>
              <Spacer x={0.5} />
              <Tag
                textColor={colors["colors/text/black"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                text={value}
                borderColor={colors["colors/borders/cells/cells"]}
              />
            </StyledRow>
          ),
      },
      averageMonthlyAmount: {
        type: "number",
        width: 250,
        valueFormatter: formatCurrency,
      },
      averagePricePerLiter: {
        type: "number",
        valueFormatter: formatCurrency,
      },
      averageQuantity: {
        type: "number",
        valueFormatter: formatLiter,
      },
      averageConsumption: {
        type: "number",
        width: 200,
        valueFormatter: formatLiterPerOneHundredMileage,
      },
      averageCarbonEmissionPerKm: {
        type: "number",
        width: 250,
        valueFormatter: formatCarbonEmissionPerKilometer,
      },
      analyticCode1: {
        type: "string",
        width: 200,
      },
      analyticCode2: {
        type: "string",
        width: 200,
      },
      transactionsWithoutMileageCount: {
        type: "number",
        width: 200,
      },
      transactionsWithoutSupportingDocumentCount: {
        type: "number",
        width: 200,
      },
      vehicleAnalyticCode: {
        type: "string",
        width: 200,
      },
      transactionsCount: {
        type: "number",
        width: 200,
      },
      ...(company?.hasAdvancedAnalytics
        ? {
            ecoDrivingScore: {
              type: "number",
              width: 150,
              valueGetter: (val: string) => {
                return val ? Number(val).toFixed(1) : "";
              },
            },
          }
        : {}),
    };
  }, [company?.hasAdvancedAnalytics, rowHoverUuid, t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <PageDS
      title={t("drivers.title")}
      toaster={<ToastContainerDS />}
      topElement={<DriverAccessRequestSummary />}
      supportButton={{
        showMail: props.otherSupportPhones ? !props.otherSupportPhones : true,
        otherPhones: props.otherSupportPhones ?? undefined,
      }}
    >
      <MaterialUiTable<keyof DriversListDisplayedType, DriversListDisplayedType>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={props.sidePanelAction}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        setRowHoverUuid={setRowHoverUuid}
        rowToSendToTop={rowToSendToTop}
      />
      {props.sidePanel ? props.sidePanel : <></>}
    </PageDS>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
